import type { SingleCoverageInformation } from "../../../../data-model/coverageInformation";
import EvCoverageEmptyState from "../evCoverageEmptyState/evCoverageEmptyState";
import EvCoverageSidebar from "../evCoverageSidebar/evCoverageSidebar";

export interface EvCoverageNoDataViewProps {
  /**
   * Order-level snapshot used for the demographics sidebar when coverage rows
   * are missing from Postgres.
   */
  orderContextSnapshot: SingleCoverageInformation;
  /** When true, renders only the empty state without the sidebar column. */
  hideSidebar?: boolean;
  /**
   * When provided, the empty state shows a Refresh action that triggers the
   * same coverage refresh as the header icon.
   */
  onRefreshClick?: () => void;
  /** True while a coverage refresh is in flight. */
  isRefreshing?: boolean;
}

/**
 * Full coverage body fallback when no insurance tiers exist in Postgres.
 * Shows order demographics on the left (when available) and a clear empty
 * state on the right instead of a blank panel.
 *
 * @param props - Order context snapshot, sidebar visibility, and refresh action
 * @returns Two-column EV layout with an empty-state main panel
 */
export const EvCoverageNoDataView = (props: EvCoverageNoDataViewProps) => {
  const {
    orderContextSnapshot,
    hideSidebar = false,
    onRefreshClick,
    isRefreshing = false,
  } = props;

  if (hideSidebar) {
    return (
      <div className="ev-coverage-unified flex h-full min-h-0 w-full flex-col pt-1 md:pt-2">
        <EvCoverageEmptyState
          variant="noCoverageData"
          className="min-h-[16rem]"
          onRefreshClick={onRefreshClick}
          isRefreshing={isRefreshing}
        />
      </div>
    );
  }

  return (
    <div className="ev-coverage-unified grid h-full min-h-0 w-full grid-cols-[3fr_7fr] gap-2 pt-1 sm:gap-3 md:gap-4 md:pt-2 lg:gap-5 lg:pt-3">
      <EvCoverageSidebar singleCoverage={orderContextSnapshot} />
      <div className="ev-coverage-unified__main flex min-h-0 min-w-0 flex-col overflow-hidden">
        <EvCoverageEmptyState
          variant="noCoverageData"
          onRefreshClick={onRefreshClick}
          isRefreshing={isRefreshing}
        />
      </div>
    </div>
  );
};

export default EvCoverageNoDataView;
